import React, { useState, useEffect } from "react";
import Tab1_UploadCV from "./Tab1_UploadCV";
import Tab2_CompleteProfile from "./Tab2_CompleteProfile";
import Tab3_WhatsApp from "./Tab3_WhatsApp";
import TabNavigation from "./TabNavigation";
import RecaptchaNotice from "./RecaptchaNotice";
import cvParserService from "../services/cvParserService";
import formSubmissionService from "../services/formSubmissionService";
import airtableService from "../services/airtableService";

const FormWizard = () => {
  const [currentTab, setCurrentTab] = useState(1);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [airtableRecordId, setAirtableRecordId] = useState(null);
  const [positions, setPositions] = useState([]);

  // Load positions on mount
  useEffect(() => {
    const loadPositions = async () => {
      try {
        const positionsData = await airtableService.getPositions();
        setPositions(positionsData);
      } catch (error) {
        setPositions([]);
      }
    };
    loadPositions();
  }, []);

  const showNotification = (message, type = "info") => {
    // Notification removed
  };

  const handleTab1Next = async () => {
    // Validate Tab 1
    if (!validateTab(1)) {
      const firstErrorField = document.querySelector(".field-error-input");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    const cvFile = files.cv;
    if (!cvFile) {
      showNotification("Please upload your CV", "error");
      return;
    }

    // Disable form and show inline loader
    setLoading(true);
    setLoadingMessage("Preparing to process your CV...");
    setProgress(0);

    try {
      // Parse CV with email, position, and position_record_id
      const result = await cvParserService.parseCV(
        cvFile,
        formData.email,
        formData.position,
        formData.position_record_id,
        (percent, message) => {
          setProgress(percent);
          setLoadingMessage(message);
        }
      );

      if (result.success && result.data) {
        // Store Airtable record ID
        if (result.recordId) {
          setAirtableRecordId(result.recordId);
        }

        // Map parsed data to form fields
        const autoFilledFields = [];
        const updates = {};

        // Fields that should NEVER be auto-filled (must be manually entered by user)
        const excludedFields = ['join', 'hiring_source', 'gfjoin', 'gf_join'];

        // Enhanced field mappings - map CV parser field names to form field names
        const fieldMappings = {
          // Name fields
          g1st_name: "first_name",
          g2nd_name: "last_name",
          first: "first_name",
          last: "last_name",

          // Contact fields
          phone: "whatsapp",
          mobile: "whatsapp",
          gwhatsapp: "whatsapp",
          whatsapp_number: "whatsapp",

          // Links
          short_url: "portfolio",
          portfolio_url: "portfolio",
          linkedin: "glinkedin",
          linkedin_url: "glinkedin",
          instagram: "ginsta",
          instagram_url: "ginsta",

          // Position
          job_position: "position",

          // Experience
          years_of_experience: "how_many_years_of_experience_do_you_have",
          experience_years: "how_many_years_of_experience_do_you_have",
          years_experience: "how_many_years_of_experience_do_you_have",
          yrs: "how_many_years_of_experience_do_you_have",

          // Education & Personal
          education: "g_education",
          education_level: "g_education",
          gender: "gmf",
          marital_status: "gfmarital",
          date_of_birth: "gage",
          birth_date: "gage",
          dob: "gage",
          age: "gage",

          // Location
          current_city: "gcity",
          current_residence: "where_do_you_currently_reside",
          residence: "where_do_you_currently_reside",

          // Languages
          spoken_languages: "gspoken_languages",
          written_languages: "gwritten_languages",
          languages: "gspoken_languages"
        };

        Object.keys(result.data).forEach((key) => {
          const value = result.data[key];

          // Skip excluded fields and empty values
          if (excludedFields.includes(key) || value === null || value === undefined || value === "") {
            return;
          }

          // Check if this key needs to be mapped to a different field name
          const targetKey = fieldMappings[key] || key;

          // Add to updates
          updates[targetKey] = value;

          if (!autoFilledFields.includes(targetKey)) {
            autoFilledFields.push(targetKey);
          }
        });

        updates._autoFilled = autoFilledFields;

        // Update form data with parsed values
        setFormData((prev) => ({ ...prev, ...updates }));

        showNotification(
          `CV parsed successfully! ${autoFilledFields.length} fields auto-filled.`,
          "success"
        );

        // Use setTimeout to ensure state update completes before tab transition
        // This prevents race condition where Tab 2 renders before parsed data is applied
        setTimeout(() => {
          setCurrentTab(2);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      } else {
        // Use the detailed error message from the service
        const errorMessage =
          result.error ||
          "Could not parse CV automatically. You can still fill the form manually.";
        showNotification(errorMessage, "warning");

        // Still proceed to Tab 2 - never block the candidate
        setTimeout(() => {
          setCurrentTab(2);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      }
    } catch (error) {
      console.error("CV upload error:", error);

      // Graceful error - never block the candidate from applying
      showNotification(
        "Could not process CV automatically. You can fill the form manually.",
        "warning"
      );

      setTimeout(() => {
        setCurrentTab(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } finally {
      setLoading(false);
      setProgress(0);
      setLoadingMessage("");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files: inputFiles } = e.target;

    if (type === "file" && inputFiles && inputFiles[0]) {
      setFiles((prev) => ({ ...prev, [name]: inputFiles[0] }));
      setFormData((prev) => ({ ...prev, [name]: inputFiles[0].name }));
    } else {
      const updates = {
        [name]: type === "checkbox" ? checked : value,
      };

      // If position is selected, also store the position_record_id
      if (name === "position" && value) {
        const selectedPosition = positions.find(p => p.title === value);
        if (selectedPosition) {
          updates.position_record_id = selectedPosition.id;
          updates.position_job_form = selectedPosition.jobForm;
        }
      }

      // If country is selected, clear city to prevent stale data
      if (name === "where_do_you_currently_reside") {
        updates.gcity = '';
      }

      setFormData((prev) => ({
        ...prev,
        ...updates,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateTab = (tab) => {
    const newErrors = {};

    if (tab === 1) {
      // Validate Tab 1 fields
      if (!formData.email || formData.email === "") {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }

      if (!formData.position || formData.position === "") {
        newErrors.position = "Please select a position";
      }

      if (!formData.fullpart_time || formData.fullpart_time === "") {
        newErrors.fullpart_time = "Please select employment type";
      }

      if (!files.cv) {
        newErrors.cv = "Please upload your CV";
      }
    }

    if (tab === 2) {
      // Validate Tab 2 fields (all required fields from airtable)
      const requiredFields = [
        "first_name",
        "last_name",
        "whatsapp",
        "nationality",
        "gspoken_languages",
        "gwritten_languages",
        "gfmarital",
        "gmf",
        "gage",
        "where_do_you_currently_reside",
        "gcity",
        "uae_residence",
        "how_many_years_of_experience_do_you_have",
        "last_position",
        "g_education",
        "glinkedin",
        "join",
        "usd_current",
        "usd_excepted",
        "hiring_source",
      ];

      requiredFields.forEach((field) => {
        const value = formData[field];

        if (Array.isArray(value)) {
          if (value.length === 0) {
            newErrors[field] = "This field is required";
          }
          return;
        }

        if (
          !value ||
          value === "" ||
          value === `Please Select` ||
          value.toString().toLowerCase().includes("please select")
        ) {
          newErrors[field] = "This field is required";
        }
      });

      // Photo validation
      if (!files.photo && !formData.photo) {
        newErrors.photo = "Please upload your photo";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateTab(2)) {
      const errorFieldNames = Object.keys(errors).map((key) => {
        return key
          .replace(/([A-Z])/g, " $1")
          .trim()
          .replace(/^./, (str) => str.toUpperCase());
      });

      const message =
        errorFieldNames.length > 0
          ? `Please complete the following required fields: ${errorFieldNames.join(
              ", "
            )}`
          : "Please complete all required fields";

      showNotification(message, "error");

      const firstErrorField = document.querySelector(".field-error-input");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    try {
      const result = await formSubmissionService.submitForm(
        formData,
        files,
        airtableRecordId
      );

      if (result.success) {
        showNotification("Application submitted successfully!", "success");
        setTimeout(() => {
          setCurrentTab(3);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 500);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      showNotification(
        "Failed to submit application. Please try again.",
        "error"
      );
    }
  };

  return (
    <div className="wizard-container">
      {/* All loading overlays and modals removed per user request */}

      {/* Tab Navigation */}
      <TabNavigation
        currentTab={currentTab}
        onTabChange={setCurrentTab}
        cvUploaded={!!files.cv}
        disabled={loading && currentTab === 1}
      />

      {/* Form Content */}
      <form onSubmit={handleSubmit}>
        {/* Tab 1: Upload CV */}
        <div className={`tab-panel ${currentTab === 1 ? "active" : ""}`}>
          <Tab1_UploadCV
            onNext={handleTab1Next}
            formData={formData}
            onChange={handleChange}
            errors={errors}
            loading={loading && currentTab === 1}
            loadingMessage={loadingMessage}
            progress={progress}
            positions={positions}
          />
        </div>

        {/* Tab 2: Complete Profile */}
        <div className={`tab-panel ${currentTab === 2 ? "active" : ""}`}>
          <Tab2_CompleteProfile
            formData={formData}
            onChange={handleChange}
            errors={errors}
            files={files}
            onFileChange={(name, file) =>
              setFiles((prev) => ({ ...prev, [name]: file }))
            }
          />

          {/* NDA Checkbox */}
          <div style={{ marginTop: "40px" }}>
            <p>
              <label className="req" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '2.5px', fontSize: '14px'}}>Non-Disclosure Agreement</label>
            </p>
            <div className="flex checkbox-text mt-20-desktop" style={{ display: 'flex', margin: "10px", flexDirection: 'row', alignItems: 'flex-start', gap: '12px' }}>
              <input
                type="checkbox"
                id="nda"
                name="nda"
                checked={formData.nda || false}
                onChange={handleChange}
                required
                style={{
                  accentColor: "var(--astudio-black)",
                  marginTop: '4px',
                  flexShrink: 0,
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              <div style={{ color: "#666666", flex: 1 }}>
                By ticking the box and clicking submit you sign our{" "}
                <a
                  href="https://www.astudio.ae/wp-content/uploads/interview-non-disclosure-confidentiality-agreement-template.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontFamily: "Neutra Text" }}
                >
                  Non-Disclosure Agreement.
                </a>
                <br />
                Applicant agrees to retain Confidential Information in
                strict confidence, to protect the security, integrity, and
                confidentiality of such information and to not permit
                unauthorized access to or unauthorized use, disclosure,
                publication, or dissemination of the hiring and
                recruitment process.
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div
            className="form-actions"
            style={{ marginTop: "40px", justifyContent: "flex-start" }}
          >
            <button type="submit" className="btn btn-primary" style={{fontFamily: '"Neutra Text"',
              letterSpacing: '1px'}}>
              LET'S GO
            </button>
          </div>

          {/* reCAPTCHA Notice */}
          <RecaptchaNotice />
        </div>

        {/* Tab 3: Continue on WhatsApp */}
        <div className={`tab-panel ${currentTab === 3 ? "active" : ""}`}>
          <Tab3_WhatsApp formData={formData} />
        </div>
      </form>
    </div>
  );
};

export default FormWizard;
